/*
 * Copyright (C) 2020-2024 The FrontDOOR Corp, Inc. All Rights Reserved.
 *
 * This software is the confidential and proprietary information of The FrontDOOR Corp, Inc.
 * ("Confidential Information").  You shall not disclose such Confidential Information and
 * shall use it only in accordance with the terms of the license agreement you entered into
 * with The FrontDOOR Corp, Inc.
 */

/**
 * Slice to handle CRUD operation for Route of Dedicated Delivery.
 *
 * @version 1.0 26th Feb 2024
 * @author gbiswajeeta
 */
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  RouteDataArgs,
  RoutePerStoreDataArgs,
} from "../../../pages/dedicatedDelivery/DdTypes";
import agent from "../../api/agent";
import {
  DdRouteDashboardUIResponse,
  DdRouteImportUIResponse,
  DdRouteProgressUIResponse,
} from "../../models/ApiResponse";
import {
  DdFdpRouteProgressDto,
  DdRouteProgressDto,
  DdRouteProgressRequest,
} from "../../models/responseModels/DdRouteProgressResponse";
import {
  DdRouteDashboardResponseWrap,
  DdRouteDtoWrap,
} from "../../models/responseModels/DdRouteResponse";

// type required for initial state.
type InitialState = {
  isLoading: boolean;
  routeData: DdRouteDashboardResponseWrap;
  routeDataPerStore: DdRouteDashboardResponseWrap;
  errorMessage: string | null;
  routeProgressData: {
    routeProgress: DdRouteProgressDto[];
    fdpRouteProgress: DdFdpRouteProgressDto[];
  };
};

// initialize initial state
const initialState: InitialState = {
  isLoading: false,
  routeData: { totalRows: 0, routes: [] },
  routeDataPerStore: { totalRows: 0, routes: [] },
  errorMessage: null,
  routeProgressData: {
    routeProgress: [],
    fdpRouteProgress: [],
  },
};

// thunk to get all routes per client
export const getRouteData = createAsyncThunk<
  DdRouteDashboardUIResponse,
  RouteDataArgs
>(
  "dedicated-delivery/overview-of-routes-response",
  async (routeDataArgs: RouteDataArgs) => {
    const response = await agent.DedicatedDeliveryAgent.getRouteData(
      routeDataArgs
    );
    return response;
  }
);

export const getRouteProgressDataThunk = createAsyncThunk<
  DdRouteProgressUIResponse,
  DdRouteProgressRequest
>("route-progress-dedicated-routes", async (args: DdRouteProgressRequest) => {
  return await agent.DedicatedDeliveryAgent.getRouteProgressData(args);
});

// thunk to get routes per store and client
export const getRouteDataPerStore = createAsyncThunk<
  DdRouteDashboardUIResponse,
  RoutePerStoreDataArgs
>(
  "dedicated-delivery/overview-of-routes-per-store-response",
  async (routeDataArgs: RoutePerStoreDataArgs) => {
    const response = await agent.DedicatedDeliveryAgent.getRouteDataPerStore(
      routeDataArgs
    );
    return response;
  }
);

// thunk to persist routes by making server call
export const storeRouteData = createAsyncThunk<
  DdRouteImportUIResponse,
  DdRouteDtoWrap
>("dedicated-delivery/stores-routes-response", async (args: DdRouteDtoWrap) => {
  const response = await agent.DedicatedDeliveryAgent.storeRouteData(args);
  return response;
});

// thunk to persist routes by making server call
export const storeRouteDataV2 = createAsyncThunk<
  DdRouteImportUIResponse,
  DdRouteDtoWrap
>(
  "dedicated-delivery/stores-routes-response-V2",
  async (args: DdRouteDtoWrap) => {
    const response = await agent.DedicatedDeliveryAgent.storeRouteDataV2(args);
    return response;
  }
);

// reducer to handle actions on routes
export const getRouteSlice = createSlice({
  name: "dedicatedDeliveryFetchRoute",
  initialState,
  reducers: {
    updateRouteProgressEstimatedTripTime(
      state,
      action: PayloadAction<{ id: string; estimatedTripTime: string }>
    ) {
      const { id, estimatedTripTime } = action.payload;
      const routeProgressItem = state?.routeProgressData?.routeProgress.find(
        (item) => item.ROUTE_ID === id
      );

      if (routeProgressItem) {
        routeProgressItem.estimatedTripTime = estimatedTripTime;
      }
    },
    updateRouteProgressOrdersUndelivered(
      state,
      action: PayloadAction<{ id: string; ordersUndeliveredUnattempted: number }>
    ) {
      const { id, ordersUndeliveredUnattempted } = action.payload;
      console.log("route progress payload", ordersUndeliveredUnattempted);
      const routeProgressItem = state?.routeProgressData?.routeProgress.find(
        (item) => item.ROUTE_ID === id
      );

      if (routeProgressItem) {
        routeProgressItem.ordersUndeliveredUnattempted = ordersUndeliveredUnattempted;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRouteData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getRouteData.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.isSuccess) {
          state.routeData = action.payload.data;
        } else {
          state.routeData.routes = [];
        }
      })
      .addCase(getRouteData.rejected, (state, action) => {
        state.isLoading = false;
        state.routeData.routes = [];
        state.errorMessage = action.error.message!;
      })
      .addCase(getRouteDataPerStore.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getRouteDataPerStore.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.isSuccess) {
          state.routeDataPerStore = action.payload.data;
        } else {
          state.routeDataPerStore.routes = [];
        }
      })
      .addCase(getRouteProgressDataThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.routeDataPerStore.routes = [];
        state.errorMessage = action.error.message!;
      })
      .addCase(getRouteProgressDataThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getRouteProgressDataThunk.fulfilled, (state, action) => {
        state.isLoading = false;

        if (action.payload.isSuccess) {
          if (Object.keys(action.payload.data).length) {
            state.routeProgressData = {
              routeProgress: action.payload.data["routeProgress"],
              fdpRouteProgress: action.payload.data["fdpRouteProgress"],
            };
          }
        } else {
          state.routeProgressData = {
            routeProgress: [],
            fdpRouteProgress: [],
          };
        }
      })
      .addCase(getRouteDataPerStore.rejected, (state, action) => {
        state.isLoading = false;
        state.routeProgressData = {
          routeProgress: [],
          fdpRouteProgress: [],
        };
      });
  },
});


// reducer to handle actions on routes
export const persistRouteSlice = createSlice({
  name: "dedicatedDeliveryPersistRoute",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(storeRouteData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(storeRouteData.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(storeRouteData.rejected, (state, action) => {
        state.isLoading = false;
        state.routeData.routes = [];
        state.errorMessage = action.error.message!;
      });
  },
});

export const { 
  updateRouteProgressEstimatedTripTime, 
  updateRouteProgressOrdersUndelivered 
} = getRouteSlice.actions;
export default getRouteSlice.reducer;
